import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Button,
  Space,
  Tag,
} from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getAllInvoiceItems } from "../../../api/accounting";

const { RangePicker } = DatePicker;
const { Option } = Select;

const InvoiceItems = ({ filters, onFilterChange, selectedBrand = null }) => {
  const [loading, setLoading] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Columns for the invoice items table
  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: (a, b) => a.invoice_number.localeCompare(b.invoice_number),
    },
    {
      title: "Bill Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      sorter: (a, b) => dayjs(a.issue_date).unix() - dayjs(b.issue_date).unix(),
    },
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      sorter: (a, b) => a.vendor_name.localeCompare(b.vendor_name),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      render: (category) => category || "N/A",
      sorter: (a, b) => {
        const catA = a.category_name || "";
        const catB = b.category_name || "";
        return catA.localeCompare(catB);
      },
    },
    {
      title: "Item Description",
      dataIndex: "description",
      key: "description",
      render: (description, record) => description || record.product_name,
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (price) => `$${parseFloat(price).toFixed(2)}`,
      sorter: (a, b) => a.unit_price - b.unit_price,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      render: (subtotal) => `$${parseFloat(subtotal).toFixed(2)}`,
      sorter: (a, b) => a.subtotal - b.subtotal,
    },
  ];

  // Fetch invoice items when filters change or on initial load
  useEffect(() => {
    fetchInvoiceItems();
  }, [filters, pagination.current, pagination.pageSize, selectedBrand]);

  const fetchInvoiceItems = async () => {
    try {
      setLoading(true);

      // Prepare filter parameters
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      // Add date range filters if available
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.start_date = filters.dateRange[0].format("YYYY-MM-DD");
        params.end_date = filters.dateRange[1].format("YYYY-MM-DD");
      }

      // Add vendor filter if available
      if (filters.partner) {
        params.vendor = filters.partner;
      }

      // Add brand filter if available
      if (selectedBrand) {
        params.brand = selectedBrand.id;
      }

      const response = await getAllInvoiceItems(params);

      setInvoiceItems(response || []);
      setPagination({
        ...pagination,
        total: response.count || 0,
      });
    } catch (error) {
      console.error("Error fetching invoice items:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
    });
  };

  return (
    <Card title="Invoice Items" className="accounting-card">
      <Table
        columns={columns}
        dataSource={invoiceItems}
        rowKey="id"
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
    </Card>
  );
};

export default InvoiceItems;
